import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { captureError } from '../common/sentry';
import {
  createQmRecord,
  getQmRecord,
  updateQmRecord,
  listQmReferenceReasons,
  listQmReferenceClosureCodes,
  listQmReferenceLocations,
  getUploadUrl,
  listAttachments,
  confirmAttachment
} from '../queries/queries';

export type QmCrudStatus = 'CREATED' | 'PENDING' | 'CLOSED';
export type QmRecordType = 'INBOUND' | 'OUTBOUND';

export type QmStatusHistoryEntry = {
  id: number;
  fromStatus: QmCrudStatus | null;
  toStatus: QmCrudStatus;
  changedBy: string;
  changedAt: string;
  note: string | null;
};

export type QmChangeHistoryEntry = {
  id: number;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  changedBy: string;
  changedAt: string;
};

export type QmRecord = {
  qmId: number;
  qmNumber: string;
  type: QmRecordType;
  status: QmCrudStatus;
  reasonCode: string;
  reasonLabel: string;
  closureCode: string | null;
  closureLabel: string | null;
  sscc: string | null;
  asn: string | null;
  poNumber: string | null;
  poLine: string | null;
  poLineId: string | null;
  itemId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  contactName: string | null;
  itemLocation: string;
  packageCount: number | null;
  packageType: string | null;
  description: string | null;
  closureNotes: string | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
  closedAt: string | null;
  statusHistory: QmStatusHistoryEntry[];
  changeHistory: QmChangeHistoryEntry[];
};

export type CreateQmRecordInput = {
  type?: QmRecordType | null;
  reasonCode: string;
  sscc?: string | null;
  asn?: string | null;
  poNumber?: string | null;
  poLine?: string | null;
  poLineId?: string | null;
  itemId?: string | null;
  supplierCode?: string | null;
  supplierName?: string | null;
  contactName?: string | null;
  itemLocation: string;
  packageCount?: number | null;
  packageType?: string | null;
  description?: string | null;
  createdBy?: string | null;
};

export type UpdateQmRecordInput = {
  qmId: number;
  status?: QmCrudStatus | null;
  reasonCode?: string | null;
  closureCode?: string | null;
  sscc?: string | null;
  asn?: string | null;
  poNumber?: string | null;
  poLine?: string | null;
  poLineId?: string | null;
  itemId?: string | null;
  supplierCode?: string | null;
  supplierName?: string | null;
  contactName?: string | null;
  itemLocation?: string | null;
  packageCount?: number | null;
  packageType?: string | null;
  description?: string | null;
  closureNotes?: string | null;
  updatedBy?: string | null;
  note?: string | null;
};

type CreateQmRecordMutation = {
  createQmRecord: QmRecord;
};

type UpdateQmRecordMutation = {
  updateQmRecord: QmRecord;
};

type GetQmRecordQuery = {
  getQmRecord: QmRecord;
};

export type QmReasonOption = {
  code: string;
  label: string;
  displayOrder: number;
};

export type QmClosureCodeOption = {
  code: string;
  label: string;
  reasonCode: string;
  displayOrder: number;
};

export type QmLocationOption = {
  code: string;
  label: string;
  displayOrder: number;
};

type ListQmReferenceReasonsQuery = {
  listQmReferenceReasons: QmReasonOption[];
};

type ListQmReferenceClosureCodesQuery = {
  listQmReferenceClosureCodes: QmClosureCodeOption[];
};

type ListQmReferenceLocationsQuery = {
  listQmReferenceLocations: QmLocationOption[];
};

export async function createQmRecordRequest(input: CreateQmRecordInput): Promise<QmRecord> {
  try {
    const result = (await API.graphql(
      graphqlOperation(createQmRecord, { input })
    )) as GraphQLResult<CreateQmRecordMutation>;

    if (!result.data?.createQmRecord) throw new Error('No data returned from createQmRecord');
    return result.data.createQmRecord;
  } catch (error) {
    console.error('Error creating QM record:', error);
    captureError(error, 'qmCrud-createQmRecord');
    throw error;
  }
}

export async function updateQmRecordRequest(input: UpdateQmRecordInput): Promise<QmRecord> {
  try {
    const result = (await API.graphql(
      graphqlOperation(updateQmRecord, { input })
    )) as GraphQLResult<UpdateQmRecordMutation>;

    if (!result.data?.updateQmRecord) throw new Error('No data returned from updateQmRecord');
    return result.data.updateQmRecord;
  } catch (error) {
    console.error('Error updating QM record:', error);
    captureError(error, 'qmCrud-updateQmRecord');
    throw error;
  }
}

export async function getQmRecordRequest({
  qmId,
  qmNumber
}: {
  qmId?: number;
  qmNumber?: string;
}): Promise<QmRecord | null> {
  try {
    const result = (await API.graphql(
      graphqlOperation(getQmRecord, { qmId, qmNumber })
    )) as GraphQLResult<GetQmRecordQuery>;

    return result.data?.getQmRecord ?? null;
  } catch (error) {
    console.error('Error retrieving QM record:', error);
    captureError(error, 'qmCrud-getQmRecord');
    throw error;
  }
}

export async function listQmReferenceReasonsRequest(): Promise<QmReasonOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceReasons)
    )) as GraphQLResult<ListQmReferenceReasonsQuery>;
    return result.data?.listQmReferenceReasons ?? [];
  } catch (error) {
    console.error('Error retrieving QM reasons:', error);
    captureError(error, 'qmCrud-listQmReferenceReasons');
    throw error;
  }
}

export async function listQmReferenceClosureCodesRequest(reasonCode?: string | null): Promise<QmClosureCodeOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceClosureCodes, { reasonCode: reasonCode ?? null })
    )) as GraphQLResult<ListQmReferenceClosureCodesQuery>;

    return result.data?.listQmReferenceClosureCodes ?? [];
  } catch (error) {
    console.error('Error retrieving QM closure codes:', error);
    captureError(error, 'qmCrud-listQmReferenceClosureCodes');
    throw error;
  }
}

export async function listQmReferenceLocationsRequest(): Promise<QmLocationOption[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listQmReferenceLocations)
    )) as GraphQLResult<ListQmReferenceLocationsQuery>;

    return result.data?.listQmReferenceLocations ?? [];
  } catch (error) {
    console.error('Error retrieving QM locations:', error);
    captureError(error, 'qmCrud-listQmReferenceLocations');
    throw error;
  }
}

export type QmAttachment = {
  id: number;
  qmId: number;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedBy: string;
  uploadedAt: string;
  downloadUrl: string;
};

export type ConfirmAttachmentInput = {
  qmId: number;
  s3Key: string;
  fileName: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  uploadedBy?: string | null;
};

type GetUploadUrlQuery = {
  getUploadUrl: { uploadUrl: string; s3Key: string };
};

type ListAttachmentsQuery = {
  listAttachments: QmAttachment[];
};

type ConfirmAttachmentMutation = {
  confirmAttachment: QmAttachment;
};

export async function getUploadUrlRequest(args: {
  qmId: number;
  fileName: string;
  mimeType?: string | null;
}): Promise<{ uploadUrl: string; s3Key: string }> {
  try {
    const result = (await API.graphql(graphqlOperation(getUploadUrl, args))) as GraphQLResult<GetUploadUrlQuery>;

    if (!result.data?.getUploadUrl) throw new Error('No data returned from getUploadUrl');
    return result.data.getUploadUrl;
  } catch (error) {
    console.error('Error getting upload URL:', error);
    captureError(error, 'qmCrud-getUploadUrl');
    throw error;
  }
}

export async function uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' }
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }
}

export async function confirmAttachmentRequest(input: ConfirmAttachmentInput): Promise<QmAttachment> {
  try {
    const result = (await API.graphql(
      graphqlOperation(confirmAttachment, { input })
    )) as GraphQLResult<ConfirmAttachmentMutation>;

    if (!result.data?.confirmAttachment) throw new Error('No data returned from confirmAttachment');
    return result.data.confirmAttachment;
  } catch (error) {
    console.error('Error confirming attachment:', error);
    captureError(error, 'qmCrud-confirmAttachment');
    throw error;
  }
}

export async function listAttachmentsRequest(qmId: number): Promise<QmAttachment[]> {
  try {
    const result = (await API.graphql(
      graphqlOperation(listAttachments, { qmId })
    )) as GraphQLResult<ListAttachmentsQuery>;

    return result.data?.listAttachments ?? [];
  } catch (error) {
    console.error('Error listing attachments:', error);
    captureError(error, 'qmCrud-listAttachments');
    throw error;
  }
}
